/**
 * Payment success — landed here after Stripe redirects back.
 * Polls /api/payments/status/{session_id} until paid (max 5 tries x 2s),
 * then shows the receipt no with a link back to the parent home.
 */
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, CircleNotch, Receipt } from "@phosphor-icons/react";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", txn: null, error: null, attempts: 0 });
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session_id", txn: null, attempts: 0 });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (stopRef.current) return;
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        const txn = res.data;
        if (txn?.payment_status === "paid" && txn?.receipt_no) {
          setState({ phase: "paid", txn, attempts, error: null });
          return;
        }
        if (txn?.status === "expired") {
          setState({ phase: "expired", txn, attempts, error: null });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "pending", txn, attempts, error: null });
          return;
        }
        setState({ phase: "polling", txn, attempts, error: null });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "error", error: formatApiErrorDetail(e?.response?.data?.detail) || e.message, txn: null, attempts });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  return (
    <DashboardLayout title="Payment" subtitle="Stripe Checkout">
      <div className="max-w-xl mx-auto vs-card p-8 text-center" data-testid="payment-success-page">
        {state.phase === "polling" && (
          <>
            <CircleNotch size={48} className="mx-auto animate-spin text-[var(--vs-secondary)] mb-3" />
            <div className="font-display text-2xl mb-1" data-testid="payment-status-text">Verifying your payment…</div>
            <div className="text-sm text-[var(--vs-text-secondary)]">Attempt {state.attempts} of {MAX_ATTEMPTS}</div>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle size={56} weight="duotone" className="mx-auto text-[var(--vs-success)] mb-3" />
            <div className="font-display text-3xl mb-1" data-testid="payment-status-text">Payment received</div>
            <div className="text-[var(--vs-text-secondary)] mb-6">
              Thank you. Receipt has been recorded against your child's fee.
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto text-left mb-6">
              <div className="vs-card p-4">
                <div className="eyebrow">Amount</div>
                <div className="font-display text-xl mt-1" data-testid="payment-amount">{fmtINR(state.txn?.amount)}</div>
              </div>
              <div className="vs-card p-4">
                <div className="eyebrow flex items-center gap-1"><Receipt size={12} /> Receipt</div>
                <div className="font-mono text-xs mt-1 break-all" data-testid="payment-receipt-no">{state.txn?.receipt_no || "—"}</div>
              </div>
            </div>
            <Button onClick={() => navigate("/app")} data-testid="payment-back-home">Back to my family</Button>
            {state.txn?.fee_id && (
              <Button
                variant="outline"
                onClick={() => window.open(`${API}/fees/${state.txn.fee_id}/receipt`, "_blank", "noopener")}
                data-testid="payment-download-receipt"
                className="ml-2"
              >
                <Receipt size={14} className="mr-1" /> Download receipt
              </Button>
            )}
          </>
        )}
        {state.phase === "pending" && (
          <>
            <CircleNotch size={48} className="mx-auto text-[var(--vs-warning)] mb-3" />
            <div className="font-display text-2xl mb-1" data-testid="payment-status-text">Payment processing</div>
            <div className="text-sm text-[var(--vs-text-secondary)] mb-6">
              Your bank is still confirming. We'll update the fee status the moment Stripe confirms — refresh in a minute or check back on your home.
            </div>
            <Button asChild variant="outline"><Link to="/app">Back to my family</Link></Button>
          </>
        )}
        {state.phase === "expired" && (
          <>
            <XCircle size={48} className="mx-auto text-[var(--vs-danger)] mb-3" weight="duotone" />
            <div className="font-display text-2xl mb-1" data-testid="payment-status-text">Payment session expired</div>
            <div className="text-sm text-[var(--vs-text-secondary)] mb-6">Please start the payment again from your home screen.</div>
            <Button onClick={() => navigate("/app")} data-testid="payment-back-home">Back to my family</Button>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-[var(--vs-danger)] mb-3" weight="duotone" />
            <div className="font-display text-2xl mb-1" data-testid="payment-status-text">Could not verify payment</div>
            <div className="text-sm text-[var(--vs-text-secondary)] mb-6">{state.error || "Please try again."}</div>
            <Button onClick={() => navigate("/app")} data-testid="payment-back-home">Back to my family</Button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
